import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import LeaveCard from "../../components/leaves/LeaveCard";
import LeaveBalancesPanel from "../../components/leaves/LeaveBalancesPanel";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Skeleton } from "../../components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Sparkles,
  CheckCircle2,
  XCircle,
  CalendarRange,
  Loader2,
  Inbox,
} from "lucide-react";
import { toast } from "sonner";
import { LEAVE_TYPE_LABEL } from "../../lib/leaveOptions";
import { daysAgoIST, formatISTDate, todayIST } from "../../lib/datetime";
import { joinParts, plural } from "../../lib/vocabulary";
import StickyHeading from "../../components/common/StickyHeading";

export default function TeamLeavesPage() {
  const { user } = useAuth();
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionMode, setActionMode] = useState(null); // {leave, kind: 'approve'|'reject'}
  const [balances, setBalances] = useState([]);
  const balanceByUser = useMemo(
    () => Object.fromEntries(balances.map((b) => [b.user_id, b])),
    [balances]
  );
  const [remark, setRemark] = useState("");
  const [acting, setActing] = useState(false);

  async function load() {
    setLoading(true);
    try {
      // `/leaves/balances` covers the viewer too (a team includes its
      // manager), so the separate own-balance fetch went with the duplicate
      // display it fed.
      const [{ data: rows }, { data: teamBal }] = await Promise.all([
        api.get("/leaves"),
        api.get("/leaves/balances"),
      ]);
      setLeaves(rows);
      setBalances(teamBal);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (user?.id) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  async function submitAction() {
    if (!actionMode) return;
    if (actionMode.kind === "reject" && !remark.trim()) {
      toast.error("Please add a reason for rejection.");
      return;
    }
    setActing(true);
    try {
      await api.put(`/leaves/${actionMode.leave.id}/${actionMode.kind}`, {
        remark: remark.trim() || null,
      });
      toast.success(actionMode.kind === "approve" ? "Leave approved" : "Leave rejected");
      setActionMode(null);
      setRemark("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setActing(false);
    }
  }

  // Own requests are visible here but are not an approval task — see Mine.
  const pending = useMemo(
    () => leaves.filter((l) => l.status === "pending" && l.sales_officer_id !== user?.id),
    [leaves, user?.id]
  );
  const decided = useMemo(() => leaves.filter((l) => l.status !== "pending"), [leaves]);

  const upcoming = useMemo(() => {
    const today = todayIST();
    // 14 days ahead, in IST, from the one clock — `new Date().setDate(+14)`
    // walked the VIEWER's calendar and could land a day out of the company's.
    const horizonIso = daysAgoIST(-14);
    return leaves
      .filter(
        (l) => l.status === "approved" && l.to_date >= today && l.from_date <= horizonIso
      )
      .sort((a, b) => a.from_date.localeCompare(b.from_date));
  }, [leaves]);

  return (
    <div className="space-y-6" data-testid="team-leaves-page">
      <StickyHeading
        icon={Sparkles}
        label="Team Leave Approvals"
        title="Team leaves"
        subtitle={joinParts([
          plural(pending.length, "pending approval"),
          `${upcoming.length} upcoming in the next 14 days`,
        ])}
        testId="team-leaves-heading"
      />

      {/* ONE COMPONENT PER FACT (RC-14). This page used to render the
          viewer's own balance TWICE: a "My leave balance" BalanceRow here and
          the same person's row inside the Leave balances panel below, because
          a team includes its manager (B6) and `/leaves/balances` returns them
          in it. Two implementations of one fact, which is how they drift. The
          stale copy — the row — is deleted rather than forked; the panel is
          this page's one balance surface, and the viewer's own tiles in full
          detail live on My leaves and on the apply form. */}

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">
          {error}
        </div>
      )}

      {/* Pending approvals */}
      <LeaveBalancesPanel balances={balances} loading={loading} />

      <section>
        <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-callisto-gold mb-3">
          Pending approval · {pending.length}
        </h2>
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-28 rounded-xl" />
            ))}
          </div>
        ) : pending.length === 0 ? (
          <div className="bg-white border border-callisto rounded-xl p-6 text-center text-sm text-callisto-muted">
            All caught up — no pending leaves.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="team-leaves-pending">
            {pending.map((l) => (
              <LeaveCard
                key={l.id}
                leave={l}
                showOfficer
                balance={balanceByUser[l.sales_officer_id]}
                action={
                  // can_approve comes from the backend's approval rule, so a
                  // submitter never sees these on their own request.
                  !l.can_approve ? null : (
                  <div className="flex gap-1.5">
                    <Button
                      size="sm"
                      onClick={() => setActionMode({ leave: l, kind: "approve" })}
                      className="h-7 px-2.5 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid={`team-leaves-approve-${l.id}`}
                    >
                      <CheckCircle2 size={12} className="mr-1" />
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setActionMode({ leave: l, kind: "reject" })}
                      className="h-7 px-2.5 text-[11px] border-red-200 text-red-700 hover:bg-red-50"
                      data-testid={`team-leaves-reject-${l.id}`}
                    >
                      <XCircle size={12} className="mr-1" />
                      Reject
                    </Button>
                  </div>
                  )
                }
              />
            ))}
          </div>
        )}
      </section>

      {/* Upcoming calendar */}
      <section>
        <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-callisto-gold mb-3 flex items-center gap-1.5">
          <CalendarRange size={12} />
          Upcoming team leaves
        </h2>
        {upcoming.length === 0 ? (
          <div className="bg-white border border-callisto rounded-xl p-6 text-center text-sm text-callisto-muted">
            Nobody is on leave in the next 14 days.
          </div>
        ) : (
          <div className="bg-white border border-callisto rounded-xl divide-y divide-callisto" data-testid="team-leaves-upcoming">
            {upcoming.map((l) => (
              <div key={l.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-callisto-navy truncate">
                    {l.sales_officer_name}
                  </p>
                  <p className="text-[11px] text-callisto-muted">
                    {joinParts([
                      LEAVE_TYPE_LABEL[l.leave_type],
                      l.from_date === l.to_date
                        ? formatISTDate(l.from_date)
                        : `${formatISTDate(l.from_date)} → ${formatISTDate(l.to_date)}`,
                    ])}
                  </p>
                </div>
                <span className="text-xs font-bold text-callisto-navy whitespace-nowrap">
                  {l.days_count}d
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Decided history */}
      <section>
        <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-callisto-gold mb-3">
          Decided · {decided.length}
        </h2>
        {decided.length === 0 ? (
          <div className="bg-white border border-callisto rounded-xl p-6 text-center text-sm text-callisto-muted">
            No decisions yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {decided.map((l) => (
              <LeaveCard key={l.id} leave={l} showOfficer />
            ))}
          </div>
        )}
      </section>

      {/* Approve/Reject dialog */}
      <Dialog
        open={!!actionMode}
        onOpenChange={(o) => {
          if (!o) {
            setActionMode(null);
            setRemark("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionMode?.kind === "approve" ? "Approve leave" : "Reject leave"}
            </DialogTitle>
            <DialogDescription>
              {joinParts([
                actionMode?.leave?.sales_officer_name,
                actionMode?.leave &&
                  `${LEAVE_TYPE_LABEL[actionMode.leave.leave_type]} Leave`,
                actionMode?.leave && plural(actionMode.leave.days_count, "day"),
              ])}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            rows={3}
            placeholder={
              actionMode?.kind === "approve"
                ? "Optional remark"
                : "Reason for rejection (required)"
            }
            className="focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
            data-testid="team-leaves-remark"
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setActionMode(null);
                setRemark("");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={submitAction}
              disabled={acting}
              className={
                actionMode?.kind === "approve"
                  ? "bg-emerald-600 hover:bg-emerald-700"
                  : "bg-red-600 hover:bg-red-700"
              }
              data-testid="team-leaves-action-confirm"
            >
              {acting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : actionMode?.kind === "approve" ? (
                "Approve"
              ) : (
                "Reject"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
